/**
 * Bridge between agent-authored HTML and the app's action handler.
 *
 * Agents write the call the way they would an inline handler:
 *
 *   <button onClick="on_action('card_id', 'c1', 'Card 1')">Card 1</button>
 *
 * but the attribute is *not* executed. Allowing `onclick` through the sanitizer
 * would re-open arbitrary script execution in the page, so the call is parsed
 * at sanitize time into `bc-*` attributes, and a single delegated listener
 * on the message container dispatches the click. The agent-facing syntax is
 * unchanged; only the mechanism is safe.
 *
 * Supported forms - argument order matches the runtime's `set_value` shape:
 *
 *   on_action(slot, value)
 *   on_action(slot, value, title)
 *   on_action(slot, value, title, nextState)
 *   on_action({slot: 'card_id', payload: 'c1', title: 'Card 1', ...})
 */

const ON_ACTION_CALL = /on_action\s*\(\s*([\s\S]*?)\s*\)\s*;?\s*$/i;

/** Attributes the parsed call is stored in. */
// Deliberately not a `data-` prefix: DOMPurify's `ALLOW_DATA_ATTR: false`
// strips every `data-*` attribute regardless of what ALLOWED_ATTR lists, so a
// `data-bc-*` name would be removed straight after the bridge set it.
export const ACTION_ATTR = 'bc-action';
const SLOT_ATTR = 'bc-slot';
const VALUE_ATTR = 'bc-value';
const TITLE_ATTR = 'bc-title';
const NEXT_STATE_ATTR = 'bc-next-state';

/**
 * Parse the argument list of an `on_action(...)` call.
 *
 * Deliberately not `eval`/`Function`: only string literals, plain numbers and a
 * flat object literal of string values are recognised. Anything else yields
 * null and the attribute is dropped, so an executable payload cannot ride in
 * through this path.
 */
function parseArguments(source) {
  const trimmed = source.trim();
  if (trimmed === '') {
    return null;
  }

  // Object-literal form: {slot: 'x', payload: 'y'}
  if (trimmed.startsWith('{')) {
    return parseObjectLiteral(trimmed);
  }

  const parts = splitTopLevel(trimmed);
  if (parts === null) {
    return null;
  }

  const values = parts.map(readLiteral);
  if (values.some(value => value === null)) {
    return null;
  }

  const [slot, payload, title, nextState] = values;
  if (slot === undefined || payload === undefined) {
    return null;
  }
  return { slot, payload, title, nextState };
}

function parseObjectLiteral(source) {
  const body = source.replace(/^\{/, '').replace(/\}$/, '');
  const parts = splitTopLevel(body);
  if (parts === null) {
    return null;
  }

  const result = {};
  for (const part of parts) {
    if (part.trim() === '') {
      continue;
    }
    const separator = part.indexOf(':');
    if (separator === -1) {
      return null;
    }
    const key = part.slice(0, separator).trim().replace(/^['"]|['"]$/g, '');
    const value = readLiteral(part.slice(separator + 1));
    if (!/^[A-Za-z_][\w-]*$/.test(key) || value === null) {
      return null;
    }
    result[key] = value;
  }

  const slot = result.slot ?? result.slotName ?? result.name;
  const payload = result.payload ?? result.value;
  if (slot === undefined || payload === undefined) {
    return null;
  }
  return { slot, payload, title: result.title, nextState: result.nextState };
}

/** Split on commas that are not inside quotes. */
function splitTopLevel(source) {
  const parts = [];
  let current = '';
  let quote = null;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];

    if (quote) {
      // A backslash escape would let a quote hide inside a literal; refuse the
      // whole call rather than try to interpret it.
      if (character === '\\') {
        return null;
      }
      if (character === quote) {
        quote = null;
      }
      current += character;
      continue;
    }

    if (character === '"' || character === "'") {
      quote = character;
      current += character;
      continue;
    }

    // Nested structures are not part of the supported syntax.
    if ('{}[]()'.includes(character)) {
      return null;
    }

    if (character === ',') {
      parts.push(current);
      current = '';
      continue;
    }
    current += character;
  }

  if (quote) {
    return null;
  }
  parts.push(current);
  return parts;
}

/** A single quoted string, number, or bare identifier-ish token. */
function readLiteral(source) {
  const value = source.trim();
  if (value === '') {
    return undefined;
  }

  const quoted = value.match(/^(['"])([\s\S]*)\1$/);
  if (quoted) {
    return quoted[2];
  }
  if (/^-?\d+(\.\d+)?$/.test(value)) {
    return value;
  }
  // Unquoted words are accepted so `on_action(card_id, c1)` still works, but
  // anything that looks like an expression is refused rather than silently
  // taken as a literal - `window.token` is a property read the author expected
  // to be evaluated, so sending the string "window.token" would be wrong.
  if (/^[\w-]+$/.test(value)) {
    return value;
  }
  return null;
}

/**
 * Move an element's `on_action(...)` handler into `bc-*` attributes.
 *
 * Called from the sanitizer hook, before the markup reaches the DOM. Returns
 * true when a call was recognised.
 */
export function extractAction(node) {
  const raw =
    node.getAttribute?.('onclick') ||
    node.getAttribute?.('data-on-action') ||
    node.getAttribute?.('data-action');

  if (!raw) {
    return false;
  }

  const match = raw.match(ON_ACTION_CALL);
  const parsed = match ? parseArguments(match[1]) : null;
  if (!parsed) {
    return false;
  }

  // Stashed on the node object rather than written as attributes here:
  // `ALLOWED_URI_REGEXP` makes DOMPurify validate every custom attribute's
  // *value* as a URI, and `set_value` / `card_id` fail that test, so attributes
  // set in this phase are stripped moments later. `applyAction` writes them in
  // the afterSanitizeAttributes phase instead, once that check has run.
  node.__bcAction = parsed;
  return true;
}

/**
 * Write a stashed action out as attributes.
 *
 * Must run in `afterSanitizeAttributes` - see the note in `extractAction`.
 */
export function applyAction(node) {
  const parsed = node.__bcAction;
  if (!parsed) {
    return false;
  }
  delete node.__bcAction;

  node.setAttribute(ACTION_ATTR, 'set_value');
  node.setAttribute(SLOT_ATTR, parsed.slot);
  node.setAttribute(VALUE_ATTR, parsed.payload);
  if (parsed.title !== undefined) {
    node.setAttribute(TITLE_ATTR, parsed.title);
  }
  if (parsed.nextState !== undefined) {
    node.setAttribute(NEXT_STATE_ATTR, parsed.nextState);
  }
  return true;
}

/**
 * Read the action back off a clicked element.
 *
 * Shapes `requestAttributes` the way the runtime expects for `set_value`, which
 * is the same contract the BUTTONS message type uses - see
 * `button-click-handler.js`.
 */
export function readAction(element) {
  const slot = element.getAttribute(SLOT_ATTR);
  const payload = element.getAttribute(VALUE_ATTR);
  if (slot === null || payload === null) {
    return null;
  }

  const title = element.getAttribute(TITLE_ATTR);
  const nextState = element.getAttribute(NEXT_STATE_ATTR);

  const requestAttributes = {
    actionType: element.getAttribute(ACTION_ATTR) || 'set_value',
    slot,
    title: title === null ? payload : title,
    payload,
  };

  if (nextState !== null) {
    requestAttributes.nextState = nextState;
  }

  return { payload, requestAttributes };
}
